import { AddCircleOutlined } from '@mui/icons-material';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { useGgaFreeSpinOptions } from '../../queries/casino';
import type { CashbackBonusFormValues, CashbackFreeSpinTierFormValues } from '../../schema';
import CashbackFreeSpinTierRow, { type CashbackFreeSpinProviderOption } from './CashbackFreeSpinTierRow';
import CashbackGoldGamesTierRow from './CashbackGoldGamesTierRow';

export const createEmptyCashbackFreeSpinTier = (
  aggregator: 'GGA' | 'GOLDGAMES' = 'GGA'
): CashbackFreeSpinTierFormValues => ({
  aggregator,
  ...(aggregator === 'GOLDGAMES' && { goldGames: { betLevel: 0, coinValue: 0, validityHours: 24 } }),
  minLossAmount: null,
  maxLossAmount: null,
  freeSpinsAwarded: null,
  betValuePerSpin: null,
  providerId: '',
  providerName: '',
  gameId: '',
  gameName: '',
});

type Props = {
  disabled?: boolean;
  aggregator?: 'GGA' | 'GOLDGAMES';
  marketplaceId?: string;
  currency?: string;
};

const CashbackFreeSpinTiers = ({ disabled = false, aggregator = 'GGA', marketplaceId = '', currency = '' }: Props) => {
  const { control } = useFormContext<CashbackBonusFormValues>();
  const optionsQuery = useGgaFreeSpinOptions({ enabled: aggregator === 'GGA' });
  const isCatalogError = aggregator === 'GGA' && optionsQuery.isError;
  const isCatalogLoading = aggregator === 'GGA' && optionsQuery.isLoading;
  const games = useMemo(() => optionsQuery.data ?? [], [optionsQuery.data]);
  const providers = useMemo<CashbackFreeSpinProviderOption[]>(() => {
    const providerMap = new Map<string, CashbackFreeSpinProviderOption>();

    games.forEach((game) => {
      const id = String(game.providerId);
      providerMap.set(id, { id, name: game.providerName ?? `Provider ${id}` });
    });

    return Array.from(providerMap.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [games]);
  const { fields, append, remove } = useFieldArray({ control, name: 'freeSpinTiers' });

  return (
    <Stack spacing={2}>
      <Typography variant='h6'>Free Spins Tier Configuration</Typography>
      {aggregator === 'GOLDGAMES' && (
        <Alert severity='info'>
          Campaigns are created automatically for qualifying players at settlement. Use a different game per tier; Gold
          Games rejects overlapping campaigns for the same game and currency. Enter quantity, amount, bet level and coin
          value manually. Gold Games validates the betting settings at settlement; acceptance outside its presets is not
          guaranteed.
        </Alert>
      )}

      {isCatalogError && (
        <Alert severity='error' action={<Button onClick={() => optionsQuery.refetch()}>Retry</Button>}>
          Unable to load GGA free-spin providers and games.
        </Alert>
      )}
      {isCatalogLoading && (
        <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={20} />
          <Typography variant='body2'>Loading free-spin providers and games…</Typography>
        </Stack>
      )}

      {fields.map((field, index) =>
        aggregator === 'GOLDGAMES' ? (
          <CashbackGoldGamesTierRow
            key={field.id}
            index={index}
            disabled={disabled}
            marketplaceId={marketplaceId}
            currency={currency}
            onRemove={() => remove(index)}
            showRemove={fields.length > 1}
          />
        ) : (
          <CashbackFreeSpinTierRow
            key={field.id}
            disabled={disabled || isCatalogError}
            games={games}
            index={index}
            onRemove={() => remove(index)}
            providers={providers}
            showRemove={fields.length > 1}
          />
        )
      )}

      {!disabled && (
        <Button
          startIcon={<AddCircleOutlined />}
          onClick={() => append(createEmptyCashbackFreeSpinTier(aggregator))}
          disabled={isCatalogLoading || isCatalogError}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Tier
        </Button>
      )}
    </Stack>
  );
};

export default CashbackFreeSpinTiers;
